"use client";

import React, { useState, useEffect } from "react";
import { Phone, Mail, Menu, X, Globe, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle, NavigationMenuContent, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const SHARED_DATA = {
  phone: "+55 (14) 3324 5757",
  email: "contato@doctorice.com.br",
  ctaLabel: "Solicitar Proposta"
};
const BRAZIL_FLAG = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/brazil_flag.png";
const USA_FLAG = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/usa_flag_icon.png";
const ITALY_FLAG = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/italy_flag.png";
function DesktopNavigation() {
  const {
    menuItems,
    loading
  } = useMenu();
  const defaultItems = [{
    id: "1",
    label: "Soluções",
    href: "/SolutionsPage"
  }, {
    id: "2",
    label: "Equipamentos",
    href: "/EquipmentPage"
  }, {
    id: "3",
    label: "Serviços",
    href: "#services"
  }, {
    id: "4",
    label: "Cases",
    href: "#cases"
  }, {
    id: "5",
    label: "Empresa",
    href: "/CompanyPage"
  }, {
    id: "6",
    label: "Contato",
    href: "/ContactPage"
  }];
  const itemsToRender = menuItems.length > 0 ? menuItems : defaultItems;
  if (loading) {
    return <div className="hidden lg:flex gap-6">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {itemsToRender.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent text-base font-medium text-foreground hover:text-ring transition-colors">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{child.label}</div>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={cn("group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:text-ring focus:text-ring focus:outline-none disabled:pointer-events-none disabled:opacity-50 relative")}>
                  <span className="relative z-10">{item.label}</span>
                  <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-ring transition-all duration-300 group-hover:w-full" />
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems
  } = useMenu();
  const defaultItems = [{
    id: "1",
    label: "Soluções",
    href: "/SolutionsPage"
  }, {
    id: "2",
    label: "Equipamentos",
    href: "/EquipmentPage"
  }, {
    id: "3",
    label: "Serviços",
    href: "#services"
  }, {
    id: "4",
    label: "Cases",
    href: "#cases"
  }, {
    id: "5",
    label: "Empresa",
    href: "/CompanyPage"
  }, {
    id: "6",
    label: "Contato",
    href: "/ContactPage"
  }];
  const itemsToRender = menuItems.length > 0 ? menuItems : defaultItems;
  return <div className={cn("fixed inset-0 z-50 bg-background/95 backdrop-blur-sm transition-all duration-300 lg:hidden flex flex-col", isOpen ? "opacity-100 pointer-events-auto translate-x-0" : "opacity-0 pointer-events-none translate-x-full")}>
      <div className="flex items-center justify-between p-4 border-b border-border/10">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/doctorice_precision_cooling_logo.png" alt="Doctorice Logo" className="h-7" />
        <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
          <X className="h-6 w-6" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto py-8 px-6 flex flex-col gap-6">
        <nav className="flex flex-col gap-4">
          {itemsToRender.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col">
              <Link to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-ring transition-colors border-b border-border/40 pb-2" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>
              {item.children && <div className="pl-4 flex flex-col gap-2 mt-2">
                  {item.children.map((child, j) => <Link key={child.id} to={child.href || "#"} className="text-sm text-muted-foreground hover:text-ring py-1" onClick={() => setIsOpen(false)} data-index={j}>
                      {child.label}
                    </Link>)}
                </div>}
            </div>)}
        </nav>
        <div className="mt-auto flex flex-col gap-4">
          <Button className="w-full bg-ring hover:bg-ring/90 text-white font-semibold" asChild>
            <Link to="#">{SHARED_DATA.ctaLabel}</Link>
          </Button>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <span>{SHARED_DATA.phone}</span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>{SHARED_DATA.email}</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function CabecalhoInstitucional() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [language, setLanguage] = useState("pt");
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    if (typeof window !== "undefined" && (window as any).i18n) {
      (window as any).i18n.changeLanguage(lang);
    }
    console.log(`Language switched to: ${lang}`);
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="cabeçalho-institucional" className="w-full fixed top-0 left-0 z-50 flex flex-col font-sans">
      
      <div className="h-10 w-full bg-primary text-primary-foreground flex items-center justify-between px-4 lg:px-8 text-xs lg:text-sm font-medium tracking-wide z-50 relative">
        <div className="flex items-center gap-6">
          <img src="https://doctorice.10web.cloud/wp-content/uploads/2026/02/doctorice_footer_logo.png" alt="Doctorice Logo" className="h-7 mr-6" />
          <Link to={`tel:${SHARED_DATA.phone}`} className="flex items-center gap-2 hover:text-accent transition-colors">
            <Phone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{SHARED_DATA.phone}</span>
          </Link>
          <Link to={`mailto:${SHARED_DATA.email}`} className="flex items-center gap-2 hover:text-accent transition-colors">
            <Mail className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{SHARED_DATA.email}</span>
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-6 w-8 p-0 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => handleLanguageChange('pt-BR')}>
              <img src="https://doctorice.10web.cloud/wp-content/uploads/2026/04/Brasil.png" alt="Português" className="h-4 w-auto" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-8 p-0 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => handleLanguageChange('en-US')}>
              <img src={USA_FLAG} alt="English" className="h-4 w-auto" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-8 p-0 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => handleLanguageChange('it-IT')}>
              <img src="https://doctorice.10web.cloud/wp-content/uploads/2026/04/italian.png" alt="Italiano" className="h-4 w-auto" />
            </Button>
          </div>
        </div>
      </div>

      
      <div className={cn("w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/90 backdrop-blur-md shadow-sm border-border/40 py-2" : "bg-background py-4")}>
        <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
          
          <Link to="/" className="flex-shrink-0 z-50">
            <img src="https://doctorice.10web.cloud/wp-content/uploads/2026/02/cropped-cropped-LOGO-DRICE-1.png" alt="Doctorice" className="mr-6 !h-[80px] !w-[240px]" />
          </Link>

          
          <MenuProvider menu_id="25" className="contents">
            <DesktopNavigation />
          </MenuProvider>

          
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <Button asChild className="bg-ring hover:bg-ring/90 text-white font-semibold shadow-md transition-all hover:shadow-lg">
              <Link to="#">{SHARED_DATA.ctaLabel}</Link>
            </Button>
          </div>

          
          <div className="lg:hidden flex items-center">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileOpen(true)} className="text-foreground">
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>

      
      <MenuProvider menu_id="25" className="contents">
        <MobileMenu isOpen={isMobileOpen} setIsOpen={setIsMobileOpen} />
      </MenuProvider>
    </header>;
}